import { useMemo, useState, useEffect } from "react";
import Chart from "react-apexcharts";
import { useData } from "../../context/DataContext";
import LoadingComponent from "../../components/Loading/LoadingComponent";
import {
    Box,
    Grid,
    Card,
    CardContent,
    Divider,
    Tooltip,
    Typography,
} from "@mui/material";
import SingleSelect from "../../components/Selections/singleSelect";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import TvOutlinedIcon from "@mui/icons-material/TvOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import * as XLSX from 'xlsx';
import { getClientTheme } from "../../theme/clientTheme";
import LevelWiseTrainingSessionReport from "./levelWiseTraningSessionReport";
import AdOptionSummaryReports from "./adOptionSummaryReports";

const Dashboard = () => {
    const {
        user_client_id,
        employees = [],
        user = {},
        userLevels = [],
        loading,
        trainingSessions = [],
        soilReports = [],
        years = [],
        levels = [],
        trainingTypes = [],
        trainingPlans = [],
        adOptionData = [],
        handleTrainingPlanReport,
    } = useData();

    const { user_level_id } = user;
    const [yearId, setYearId] = useState(null);
    const [levelId, setLevelId] = useState(null);
    const [trainingTypeId, setTrainingTypeId] = useState(null);

    const handleInputChange = async (name, value) => {
        let newYear = yearId;
        let newLevel = levelId;
        let newType = trainingTypeId;

        if (name === "yearId") {
            newYear = value;
            setYearId(value);
        }

        if (name === "levelId") {
            newLevel = value;
            setLevelId(value);
            await handleTrainingPlanReport(value);
        }

        if (name === "trainingTypeId") {
            newType = value;
            setTrainingTypeId(value);
        }
    };

    /** ---------- DEFAULT SELECT VALUES ---------- **/
    useEffect(() => {
        years?.length && setYearId(years[0]?.value);
        levels?.length && setLevelId(levels[0]?.value);
        trainingTypes?.length && setTrainingTypeId(trainingTypes[0].value);
    }, [years, levels, trainingTypes, user_client_id, trainingPlans]);

    /** ---------- EMPLOYEE INFO ---------- **/
    const employeesData = useMemo(() => {
        return employees
            .filter((e) => e.level_id > user_level_id)
            .map((employee) => ({
                ...employee,
                user_level_name:
                    userLevels.find((l) => l.value === employee.level_id)?.label || "",
            }));
    }, [employees, user_level_id, userLevels]);

    const employeeSeries = useMemo(
        () => [
            {
                name: "Employee Data",
                data: employeesData.map((e) => e?.data?.length || 0),
            },
        ],
        [employeesData]
    );
    const employeeNames = employeesData.map(item => item.user_level_name || `Employee ${item.id}`);
    const employeeCounts = employeesData.map(item => item.data?.length || 0);

    const baseColors = ['#FF4560', '#008FFB', '#00E396', '#775DD0', '#FEB019'];
    const dynamicColors = employeeCounts.map((_, idx) => baseColors[idx % baseColors.length]);

    const employeeChartOptions = {
        chart: { type: 'bar', stacked: false, toolbar: { show: false } },
        colors: dynamicColors,
        xaxis: { categories: employeeNames, labels: { show: false } },
        yaxis: { min: 0, labels: { formatter: val => val.toFixed(0), show: true } },
        stroke: { curve: 'smooth', width: 1 },
        markers: { size: 1 },
        plotOptions: { bar: { columnWidth: '50%', distributed: true } },
        dataLabels: { enabled: true }
    };

    /** ---------- TRAINING SESSION FILTER ---------- **/
    const filteredSessions = useMemo(
        () =>
            trainingSessions.filter(item =>
                +item.levelId === +levelId &&
                +(item.year_id ?? item.year) === +yearId &&
                +item.training_type_id === +trainingTypeId &&
                (item.training_content_id == null || item.training_content_id != null)
            ),
        [trainingSessions, levelId, yearId, trainingTypeId]
    );


    const trainingSeries = [
        {
            name: "Training Session",
            data: [
                filteredSessions.length,
                filteredSessions.filter((i) => i.training_Status_id === 1).length,
                filteredSessions.filter((i) => i.training_Status_id === 2).length,
                filteredSessions.filter((i) => i.training_Status_id === 3).length,
            ],
        },
    ];

    const trainingChartColors = ['#FF4560', '#008FFB', '#00E396', '#775DD0'];
    const trainingCategories = ['Total Sessions', 'Created', 'In Process', 'Completed'];

    const trainingSessionChartOptions = {
        chart: { type: 'bar', height: 350, stacked: false, toolbar: { show: false } },
        plotOptions: { bar: { horizontal: false, distributed: true } },
        dataLabels: { enabled: true },
        xaxis: { categories: trainingCategories, labels: { show: false } },
        yaxis: { min: 0, labels: { formatter: val => val.toFixed(0), show: true } },
        colors: trainingChartColors,
    };


    /** ---------- AdOption REPORT ---------- **/
    const adOptionYearData = useMemo(
        () => adOptionData.filter((i) => +i.crop_year_id === +yearId),
        [adOptionData, yearId]
    );

    const villageCount = useMemo(
        () =>
            new Set(
                adOptionYearData.filter(i => i.village_id && i.level_id === levelId)
                    .map(i => i.village_id)
            ).size,
        [adOptionYearData, levelId]
    );

    const groupCount = useMemo(
        () =>
            new Set(
                adOptionData
                    .filter(i => i.group_id && i.level_id === levelId)
                    .map(i => i.group_id)
            ).size,
        [adOptionData, levelId, yearId]
    );

    /** ---------- PIE CHART ---------- **/
    const adoptionPieOptions = {
        chart: { type: "pie" },
        labels: levelId === 1 ? [`Village ${villageCount}`] : [`Groups ${groupCount}`],
        legend: {
            position: "bottom",
            formatter: (label) => label,
        },
        dataLabels: { enabled: false },
        colors: levelId === 1 ? ["#00E396"] : ["#008FFB"],
    };

    const adoptionPieSeries = levelId === 1 ? [villageCount] : [groupCount];

    // Village Details
    const yearsSoilData = soilReports.filter((item) => +item.year_id === +yearId)
    const data = yearsSoilData.filter((item) => +item.report_level_id === 1 && +item.year_id === +yearId);
    const villageData = data.filter(item => item.ff_group_id === null && item.farmer_id === null).reduce((unique, item) => {
        if (!unique.some(uniqueItem => uniqueItem.village_id === item.village_id)) {
            unique.push(item);
        }
        return unique;
    }, []);

    // Village Wise Farmer Detaiils
    const data1 = yearsSoilData.filter((item) => +item.report_level_id === 1 && +item.year_id === +yearId);
    const farmerData = data1.filter(item => item.village_id !== null && item.farmer_id !== null);

    // Group Details
    const groupData = yearsSoilData.filter((item) => item.report_level_id === 2 && item.year_id === +yearId);

    // OverAll Count In Soil Report
    const overAllCount = (villageData?.length || 0) + (farmerData?.length || 0) + (groupData?.length || 0);

    const soilReportDetails = [
        ...user?.level_id === null && user?.level_id === null ? [{ label: "Total Villages", value: villageData?.length }, { label: "Total Groups", value: groupData?.length }] : [],
        ...user?.level_id === 1 ? [{ label: "Total Villages", value: villageData?.length }] : [],
        ...user?.level_id === 2 ? [{ label: "Total Groups", value: groupData?.length }] : [],
        { label: "Total Farmers", value: farmerData?.length }
    ];

    /** ---------- CARDS ---------- **/
    const cards = [
        ...(user_level_id <= 2
            ? [
                {
                    title: "Employee Details",
                    value: employeesData.reduce((s, e) => s + (e?.data?.length || 0), 0),
                    subtitle: employeesData.map((e) => ({
                        label: e.user_level_name,
                        value: e?.data?.length || 0,
                    })),
                    icon: <PersonOutlineIcon />,
                    iconBg: "#E8F3E8",
                    color: "#1E88E5",
                },
            ]
            : []),
        {
            title: "Training Plans",
            value: trainingPlans.length,
            subtitle: [
                {
                    label: "Farmer Training",
                    value: trainingPlans.filter((i) => i.training_type_id === 1).length,
                },
                {
                    label: "Special Training",
                    value: trainingPlans.filter((i) => i.training_type_id === 2).length,
                },
            ],
            icon: <MenuBookOutlinedIcon />,
            iconBg: "#F3ECE6",
            color: "#43A047",
        },
        {
            title: "Training Sessions",
            value: filteredSessions.length,
            subtitle: [
                { label: "Created", value: trainingSeries[0].data[1] },
                { label: "In Process", value: trainingSeries[0].data[2] },
                { label: "Completed", value: trainingSeries[0].data[3] },
            ],
            icon: <TvOutlinedIcon />,
            iconBg: "#E8F0FF",
            color: "#FB8C00",
        },
        {
            title: "Soil Reports",
            value: overAllCount || 0,
            subtitle: soilReportDetails || [],
            icon: <DescriptionOutlinedIcon />,
            iconBg: "#FFF1E6",
            color: "#7E57C2"
        },
    ];

    const dashboardCharts = [
        { id: 1, title: "Employee Information", options: employeeChartOptions, type: "bar", height: 200, series: employeeSeries ? employeeSeries : [] },
        {
            id: 2, title: "Training Session Status", options: trainingSessionChartOptions, type: "bar", height: 200, series: trainingSeries?.some(s => s.data?.some(v => v > 0))
                ? trainingSeries
                : []
        },
        {
            id: 3, title: "Village & Group Wise Adoption", options: adoptionPieOptions, type: "pie", height: 260, series: adoptionPieSeries?.reduce((a, b) => a + b, 0) === 0
                ? []
                : adoptionPieSeries
        },
    ];

    const restrictedCharts =
        user_level_id === 4 ? dashboardCharts.slice(1) : dashboardCharts;

    if (loading) return <LoadingComponent />;

    const NoDataUI = () => (
        <Box
            sx={{
                height: "100%",
                minHeight: 180,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                color: "text.secondary",
            }}
        >
            <BarChartOutlinedIcon sx={{ fontSize: 40, mb: 1, opacity: 0.5 }} />
            <Typography variant="subtitle1" fontWeight={600}>
                No Data Found
            </Typography>
            <Typography variant="caption">
                Nothing to visualize yet
            </Typography>
        </Box>
    );

    const downloadTrainingSessionExcel = (value) => {
        const { label } = value;

        let excelDownloadData = [];
        if (label === "traingSessionTotal") {
            excelDownloadData = filteredSessions;
        } else if (label === "Created") {
            excelDownloadData = filteredSessions.filter(
                (i) => i.training_Status_id === 1
            );
        } else if (label === "In Process") {
            excelDownloadData = filteredSessions.filter(
                (i) => i.training_Status_id === 2
            );
        } else {
            excelDownloadData = filteredSessions.filter(
                (i) => i.training_Status_id === 3
            );
        }

        // ✅ Excel headers
        const formattedData = excelDownloadData.map((item) => ({
            "Village Name": item.village_names ?? "-",
            "Year": item.year_name ?? "-",
            "Training Status": item.training_status ?? "-",
        }));

        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.json_to_sheet(formattedData);
        XLSX.utils.book_append_sheet(workbook, worksheet, "Training Data");
        XLSX.writeFile(workbook, `${label}_Training_Report.xlsx`);
    };


    const downloadSoilReportExcel = (value) => {
        const { label } = value;

        let excelDownloadData = [];
        if (label === "soilReportTotal") {
            excelDownloadData = [...(villageData || []), ...(farmerData || []), ...(groupData || []),].map((item) => ({
                "Village Name": item.village_name ?? "-",
                "Group Name": item.group_name ?? "-",
                "Year": item.year ?? "-",
                "Farmer Name": item.farmer_name ?? "-",
                "Fatmer Mobile": item.farmer_mobile ?? "-"
            }));
        } else if (label === "Total Villages") {
            excelDownloadData = villageData.map((item) => ({
                "Village Name": item.village_name ?? "-",
                "Year": item.year ?? "-",
            }));
        } else if (label === "Total Farmers") {
            excelDownloadData = farmerData.map((item) => ({
                "Village Name": item.village_name ?? "-",
                "Year": item.year ?? "-",
                "Farmer Name": item.farmer_name ?? "-",
                "Fatmer Mobile": item.farmer_mobile ?? "-",
            }));
        } else {
            excelDownloadData = groupData.map((item) => ({
                "Group Name": item.group_name ?? "-",
                "Year": item.year ?? "-",
                "Farmer Name": item.farmer_name ?? "-",
                "Fatmer Mobile": item.farmer_mobile ?? "-",
            }));
        }

        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.json_to_sheet(excelDownloadData);
        XLSX.utils.book_append_sheet(workbook, worksheet, "Soil Report Data");
        XLSX.writeFile(workbook, `${label}_Soil_Report.xlsx`);
    };

    const clientTheme = getClientTheme(user_client_id);

    return (
        <Box>
            <Grid container spacing={1} sx={{ display: "flex", justifyContent: "space-between" }}>
                <Grid item size={{ xs: 12, md: 6 }} mt={1}>
                    <Typography
                        variant="h6"
                        sx={{ color: "green", display: "flex", alignItems: "center", gap: 1 }}
                    >
                        <FilterAltIcon sx={{ color: clientTheme.iconColor, }} /> <span style={{ color: clientTheme.darkColor, }}>Filters</span>
                    </Typography>
                </Grid>
                <Grid item size={{ xs: 12, md: 6 }}>
                    <Grid container spacing={1}>
                        {
                            [
                                { id: 1, label: "Years", value: yearId, name: "yearId", options: years },
                                {
                                    id: 2, label: "Levels", value: levelId, name: "levelId", options: user_client_id === 2
                                        ? levels.filter(item => item.value === 2)
                                        : levels
                                },
                                { id: 3, label: "Training Types", value: trainingTypeId, name: "trainingTypeId", options: trainingTypes },
                            ].map((field, index) => (
                                <Grid item size={{ xs: 12, md: 4 }} key={index}>
                                    <SingleSelect
                                        name={field.name}
                                        label={field.label}
                                        options={field.options}
                                        value={field.value}
                                        disabled={field.disabled}
                                        onChange={handleInputChange}
                                        error={field.error}
                                        formHelperText={true}
                                    />
                                </Grid>
                            ))
                        }
                    </Grid>
                </Grid>
            </Grid>

            <Divider sx={{ my: 1, bgcolor: '#f5f1f1ff' }} />

            <Grid container spacing={2} mt={2} mb={2}>
                {cards.map((card, index) => {
                    return (
                        <Grid item size={{ xs: 12, md: user_level_id === 1 || user_level_id === 2 ? 3 : 4 }} key={index}>
                            <Card
                                elevation={6}
                                sx={{
                                    cursor: "pointer",
                                    borderRadius: 3,
                                    height: "100%",
                                    border: "1px solid var(--btn-3)",
                                    transition: "all 0.3s ease",
                                    boxShadow: 6,
                                    "&:hover": {
                                        transform: "translateY(-3px)",
                                        boxShadow: `0 10px 18px ${card.color}66`,
                                    },
                                }}
                            >
                                <CardContent>
                                    {/* Top Row */}
                                    <Box
                                        display="flex"
                                        justifyContent="space-between"
                                        alignItems="center"
                                        mb={2}
                                    >
                                        {/* Left Icon */}
                                        <Box
                                            sx={{
                                                width: 44,
                                                height: 44,
                                                borderRadius: 2,
                                                backgroundColor: card.iconBg,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                            }}
                                        >
                                            {card.icon}
                                        </Box>

                                        {/* Center Value + Title */}
                                        <Box textAlign="end">
                                            <Typography variant="body2" color="text.secondary">
                                                {card.title}
                                            </Typography>
                                            <Typography variant="h5" fontWeight="bold">
                                                {
                                                    card.title === "Training Sessions" ? (
                                                        <Tooltip title={card.value === 0 ? "" : "Excel Download"}>
                                                            <Box onClick={() => card.value === 0 ? null : downloadTrainingSessionExcel({ label: "traingSessionTotal", value: card.value })}>
                                                                {card.value}
                                                            </Box>
                                                        </Tooltip>
                                                    ) : card.title === "Soil Reports" ? (
                                                        <Tooltip title={card.value === 0 ? "" : "Excel Download"}>
                                                            <Box onClick={() => card.value === 0 ? null : downloadSoilReportExcel({ label: "soilReportTotal", value: card.value })}>
                                                                {card.value}
                                                            </Box>
                                                        </Tooltip>
                                                    ) : (
                                                        <>{card.value}</>
                                                    )
                                                }
                                            </Typography>
                                        </Box>
                                    </Box>

                                    {/* Bottom Stats */}
                                    {card.subtitle.map((item, i) => {
                                        return (
                                            <Box
                                                key={i}
                                                display="flex"
                                                justifyContent="space-between"
                                                mt={0.5}
                                            >
                                                <Typography variant="body2" color="text.secondary">
                                                    {item.label}
                                                </Typography>
                                                <Typography variant="body2" fontWeight={600}>
                                                    {
                                                        card.title === "Training Sessions" ?
                                                            <Tooltip title={item.value === 0 ? "" : "Excel Download"}>
                                                                <Box onClick={() => item.value === 0 ? null : downloadTrainingSessionExcel(item)}>
                                                                    {item.value}
                                                                </Box>
                                                            </Tooltip> : card.title === "Soil Reports" ? (
                                                                <Tooltip title={item.value === 0 ? "" : "Excel Download"}>
                                                                    <Box onClick={() => item.value === 0 ? null : downloadSoilReportExcel(item)}>
                                                                        {item.value}
                                                                    </Box> </Tooltip>
                                                            ) : <>{item.value}</>
                                                    }
                                                </Typography>
                                            </Box>
                                        )
                                    })}
                                </CardContent>
                            </Card>
                        </Grid>
                    )
                })}
            </Grid>

            <Divider sx={{ my: 1, bgcolor: '#f5f1f1ff' }} />

            <Grid container spacing={1} mb={2}>
                {restrictedCharts.map(({ id, options, title, type, height, series }) => {

                    return (
                        <Grid item size={{ xs: 12, md: 4 }}>
                            <Card
                                key={id}
                                elevation={6}
                                sx={{
                                    boxShadow: 6,
                                    borderRadius: 3,
                                    height: "100%",
                                    border: "1px solid var(--btn-3)",
                                    transition: "all 0.3s ease",
                                    "&:hover": {
                                        boxShadow: "0px 10px 28px rgba(0,0,0,0.12)",
                                        transform: "translateY(-2px)",
                                    },
                                }}
                            >
                                {/* Header */}
                                <Box
                                    sx={{
                                        px: 1,
                                        py: 1.5,
                                        borderBottom: "1px solid #e0e0e0",
                                        textAlign: "center",
                                        backgroundColor: clientTheme.activeBg,
                                        color: "#FFF"
                                    }}
                                >
                                    <Typography
                                        variant="subtitle1"
                                        fontWeight="bold"
                                        textTransform="uppercase"
                                    >
                                        {title}
                                    </Typography>
                                </Box>

                                {/* Body */}
                                <CardContent>
                                    {series?.length > 0 ? (
                                        id === 1 ? (
                                            series.some(s => Array.isArray(s.data) && s.data.length > 0) ? (
                                                <Chart options={options} series={series} type={type} height={height} />
                                            ) : (
                                                <NoDataUI />
                                            )
                                        ) : (
                                            <Chart options={options} series={series} type={type} height={height} />
                                        )
                                    ) : (
                                        <NoDataUI />
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>
                    )
                })}
            </Grid>



            <Grid container spacing={1}>
                <Grid item size={{ xs: 12, md: 12 }} mt={1}>
                    <Card elevation={6}>
                        <Box
                            sx={{
                                px: 1,
                                py: 1.5,
                                textAlign: "center",
                                backgroundColor: clientTheme.activeBg,
                                color: "#FFF",
                            }}
                        >
                            <Typography variant="subtitle1" fontWeight="bold" textTransform="uppercase">
                                {levelId === 1 ? "Village" : "Group"} Wise Training's Summary
                            </Typography>
                        </Box>
                        <CardContent>
                            <LevelWiseTrainingSessionReport user_client_id={user_client_id} levelId={levelId} trainingTypeId={trainingTypeId} />
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <Grid container spacing={1}>
                <Grid item size={{ xs: 12, md: 12 }} mt={1}>
                    <Card elevation={6}>
                        <Box
                            sx={{
                                px: 1,
                                py: 1.5,
                                textAlign: "center",
                                backgroundColor: clientTheme.activeBg,
                                color: "#FFF",
                            }}
                        >
                            <Typography variant="subtitle1" fontWeight="bold" textTransform="uppercase">
                                {levelId === 1 ? "Village" : "Group"} Wise Adoption Summary
                            </Typography>
                        </Box>
                        <CardContent>
                            <AdOptionSummaryReports yearId={yearId} levelId={levelId} />
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>


        </Box>
    );
};
export default Dashboard;